import { useCallback } from 'react';
import { useDebounceFn } from 'ahooks';

import { useQueryClient } from '@zg-rentals/react-query5';
import { RentalManagerPropertyAddressRentZestimateWebApiHandler_rentZestimate_queryOptions } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { formatAddress } from '@zg-rentals/zillow-rental-manager/utils';

import { fetchListingDetails } from './queries/properties';

import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';

const HOVER_PREFETCH_DELAY_MS = 150;

export const getRentZestimateParams = (listingDetails?: GetUserPropertyDetailsResponseWebDto) => ({
  fullAddress: formatAddress({
    street: listingDetails?.street,
    city: listingDetails?.city,
    state: listingDetails?.state,
    zip: listingDetails?.zip,
  }).formattedCompleteAddress,
  unit: listingDetails?.unit,
});

export const getRentZestimateMeta = ({ fullAddress, unit }: ReturnType<typeof getRentZestimateParams>) => ({
  tags: ['rentZestimate'],
  context: {
    api: 'rentZestimate',
    details: { fullAddress, unit },
  },
});

// Starts the rent modal's requests early (e.g. when a row's menu opens) so it opens without waiting on them.
export const usePrefetchMonthlyRent = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (listingAlias: string) => {
      void fetchListingDetails(queryClient, listingAlias)
        .then((listingDetails) => {
          const rentZestimateParams = getRentZestimateParams(listingDetails);
          if (!rentZestimateParams.fullAddress) return;
          return queryClient.prefetchQuery({
            ...RentalManagerPropertyAddressRentZestimateWebApiHandler_rentZestimate_queryOptions(rentZestimateParams),
            meta: getRentZestimateMeta(rentZestimateParams),
          });
        })
        .catch(() => {});
    },
    [queryClient],
  );
};

// Hover waits briefly so sweeping the pointer down a column doesn't fetch every row; focus fetches right away.
export const usePrefetchMonthlyRentOnHover = () => {
  const prefetchMonthlyRent = usePrefetchMonthlyRent();
  const { run, cancel } = useDebounceFn(prefetchMonthlyRent, { wait: HOVER_PREFETCH_DELAY_MS });

  return useCallback(
    (listingAlias: string) => ({
      onPointerEnter: () => run(listingAlias),
      onPointerLeave: cancel,
      onFocus: () => prefetchMonthlyRent(listingAlias),
    }),
    [run, cancel, prefetchMonthlyRent],
  );
};
